import { Controller } from "@hotwired/stimulus"
import { tw } from "../helpers"

// Renders one polyline PER DEVICE for a vehicle's historical GPS route
// (its own tracker plus any attached Equipment's tracker) on Google Maps,
// plus an optional points-of-interest (ElectronicFence) overlay and a
// per-device animated replay marker driven by each DETALHE tab's
// play/pause control (see percurso_replay_controller.js).
//
// Refactored from a multi-vehicle/multi-track comparison view to a
// single-vehicle, multi-device "Percurso" detail map — see
// openspec/changes/refactor-percurso-detail-screen.
export default class extends Controller {
  static values = {
    apiKey:        String,
    mapId:         { type: String, default: 'DEMO_MAP_ID' },
    tracks:        { type: Object, default: {} },   // { [key]: { label, color, points, stops, drawPolyline } }
    showFences:    { type: Boolean, default: false },
    fencesJsonUrl: String
  }
  static targets = ['mapContainer', 'mapTypeDropdown', 'mapTypeMenu',
                     'fullscreenBtn', 'fullscreenExpandIcon', 'fullscreenCollapseIcon', 'legend']

  // Below this zoom level, per-position arrow markers are replaced by the
  // much cheaper IconSequence drawn directly on the polyline (see
  // drawTracks) — with the whole route zoomed out, hundreds/thousands of
  // individual Marker objects both look like noise and are the main cost.
  static MIN_ZOOM_FOR_POSITION_MARKERS = 14

  connect() {
    this.polylines       = {}
    this.pointMarkers     = {}
    this.stopMarkers      = {}
    this.positionMarkers  = {}
    this.trackPaths       = {}
    this.trackVisible     = {}
    this.fenceMarkers     = []
    this.replayMarkers    = {}
    this.replayIndexes    = {}
    this.replayTimers     = {}
    this.infoWindow       = null
    this.mapInitialized   = false
    this.currentMapType   = 'roadmap'

    this.boundMapsLoaded       = this.handleMapsLoaded.bind(this)
    this.boundFullscreenChange = this.handleFullscreenChange.bind(this)
    document.addEventListener('google-maps-loaded', this.boundMapsLoaded)
    document.addEventListener('fullscreenchange',   this.boundFullscreenChange)

    this.initOrLoad()
  }

  disconnect() {
    document.removeEventListener('google-maps-loaded', this.boundMapsLoaded)
    document.removeEventListener('fullscreenchange',   this.boundFullscreenChange)
    this.pauseAll()
    this.clearTracks()
    this.clearFences()
    if (this.infoWindow) { this.infoWindow.close(); this.infoWindow = null }
    if (this.map) {
      google.maps.event.clearInstanceListeners(this.map)
      this.map = null
    }
    this.mapInitialized = false
  }

  // ── Initialization ────────────────────────────────────────────────────────

  initOrLoad() {
    if (window.google?.maps?.MapTypeId) {
      this.initMap()
      return
    }

    if (document.body.getAttribute('data-google-maps-loaded') === 'true') {
      setTimeout(() => this.initOrLoad(), 100)
      return
    }
  }

  handleMapsLoaded() {
    this.initMap()
  }

  initMap() {
    if (this.mapInitialized) return
    if (!this.hasMapContainerTarget) return
    if (!window.google?.maps?.Map) return

    this.map = new google.maps.Map(this.mapContainerTarget, {
      center:            { lat: -14, lng: -55 },
      zoom:              5,
      minZoom:           4,
      mapTypeId:         'roadmap',
      mapId:             this.mapIdValue,
      mapTypeControl:    false,
      fullscreenControl: false,
      streetViewControl: false,
      zoomControl:       false,
      rotateControl:     false,
      scaleControl:      false
    })

    this.infoWindow     = new google.maps.InfoWindow()
    this.mapInitialized = true
    this.injectInfoWindowStyles()

    this.map.addListener('idle', () => this.updatePositionMarkers())

    if (Object.keys(this.tracksValue).length > 0) this.drawTracks()
    if (this.showFencesValue) this.loadFences()
  }

  // Inject one-time CSS overrides for the Google Maps InfoWindow chrome.
  // `background: #fff !important` on `.gm-style-iw-c` is load-bearing:
  // monitoring/vehicles.scss sets a global dark background on that same
  // selector for its own balloon, with no scoping — this page's content
  // assumes a white bubble (text-gray-900/400 values), so without this
  // override the leaked dark background makes the text unreadable.
  injectInfoWindowStyles() {
    if (document.getElementById('prev-routes-iw-styles')) return
    const style = document.createElement('style')
    style.id = 'prev-routes-iw-styles'
    style.textContent = `
      .gm-style .gm-style-iw-c {
        padding: 0 !important;
        border-radius: 12px !important;
        box-shadow: none !important;
        background: #fff !important;
      }
      .gm-style .gm-style-iw-d { padding: 0 !important; }
      .gm-style .gm-style-iw-chr {
        position: absolute !important; top: 16px !important; right: 16px !important; height: auto !important;
      }
      .gm-style .gm-style-iw-chr button { width: 22px !important; height: 22px !important; opacity: 0.5 !important; }
      .gm-style .gm-style-iw-chr button:hover { opacity: 1 !important; }
      .gm-style .gm-style-iw-t::after { display: none !important; }
    `
    document.head.appendChild(style)
  }

  // ── Reactive value changes ────────────────────────────────────────────────

  tracksValueChanged() {
    if (!this.mapInitialized) return
    this.pauseAll()
    this.clearTracks()
    if (Object.keys(this.tracksValue).length > 0) this.drawTracks()
  }

  // ── Track drawing (one polyline per device) ────────────────────────────────

  drawTracks() {
    const allPoints = []

    Object.entries(this.tracksValue).forEach(([ deviceKey, track ]) => {
      const validPositions = (track.points || []).filter(p => p.lat && p.lng)
      if (!track.drawPolyline || validPositions.length < 2) return

      const path = validPositions.map(p => ({ lat: parseFloat(p.lat), lng: parseFloat(p.lng) }))
      allPoints.push(...path)

      const polyline = new google.maps.Polyline({
        path,
        geodesic:      true,
        strokeColor:   track.color,
        strokeOpacity: 0.95,
        strokeWeight:  5,
        map:           this.map,
        zIndex:        2,
        // Cheap, always-on direction cue drawn as part of the line itself
        // (no per-position Marker objects) — the only indicator shown
        // below MIN_ZOOM_FOR_POSITION_MARKERS, and a helpful backdrop
        // even above it.
        icons: [ {
          icon: {
            path:         google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
            scale:        3,
            fillColor:    '#ffffff',
            fillOpacity:  1,
            strokeColor:  track.color,
            strokeWeight: 1.5
          },
          offset: '0',
          repeat: '60px'
        } ]
      })
      polyline.addListener('click', event => {
        const nearest = this.findNearestPosition(validPositions, event.latLng)
        if (nearest) this.showInfoWindow(event.latLng, nearest, track, deviceKey)
      })
      this.polylines[deviceKey] = polyline
      this.trackPaths[deviceKey] = { path, validPositions, track }
      this.trackVisible[deviceKey] = true

      const startMarker = this.createEndpointMarker(path[0], 'A', track, deviceKey)
      startMarker.addListener('click', () => this.showInfoWindow(startMarker.position, validPositions[0], track, deviceKey))

      const endMarker = this.createEndpointMarker(path[path.length - 1], 'B', track, deviceKey)
      endMarker.addListener('click', () => this.showInfoWindow(endMarker.position, validPositions[validPositions.length - 1], track, deviceKey))

      this.pointMarkers[deviceKey] = [ startMarker, endMarker ]

      this.stopMarkers[deviceKey] = (track.stops || []).map(stop => this.createStopMarker(stop, track, deviceKey))
    })

    this.renderLegend()
    if (allPoints.length) this.fitBounds(allPoints)
  }

  // One row per device (color swatch + label + checkbox) so a busy
  // multi-device route can have individual lines hidden instead of
  // always showing every track at once.
  renderLegend() {
    if (!this.hasLegendTarget) return

    const deviceKeys = Object.keys(this.trackPaths)
    this.legendTarget.classList.toggle('hidden', deviceKeys.length < 2)
    this.legendTarget.innerHTML = ''

    deviceKeys.forEach(deviceKey => {
      const { track } = this.trackPaths[deviceKey]

      const row = document.createElement('label')
      row.className = tw('flex', 'items-center', 'gap-1.5', 'py-0.5', 'cursor-pointer', 'select-none')

      const checkbox = document.createElement('input')
      checkbox.type = 'checkbox'
      checkbox.checked = this.trackVisible[deviceKey] !== false
      checkbox.className = tw('w-3.5', 'h-3.5', 'rounded', 'cursor-pointer')
      checkbox.addEventListener('change', () => this.toggleTrack(deviceKey, checkbox.checked))
      row.appendChild(checkbox)

      const swatch = document.createElement('span')
      swatch.className = tw('w-2.5', 'h-2.5', 'rounded-full', 'flex-shrink-0')
      swatch.style.backgroundColor = track.color || '#6B7280'
      row.appendChild(swatch)

      const label = document.createElement('span')
      label.className = tw('text-gray-700', 'dark:text-gray-200', 'whitespace-nowrap')
      label.textContent = track.label
      row.appendChild(label)

      this.legendTarget.appendChild(row)
    })
  }

  // Shows/hides one device's whole track (polyline + its endpoint, stop
  // and position markers) without touching the others.
  toggleTrack(deviceKey, visible) {
    this.trackVisible[deviceKey] = visible

    const polyline = this.polylines[deviceKey]
    if (polyline) polyline.setMap(visible ? this.map : null)

    ;(this.pointMarkers[deviceKey] || []).forEach(marker => { if (marker) marker.map = visible ? this.map : null })
    ;(this.stopMarkers[deviceKey] || []).forEach(marker => { if (marker) marker.map = visible ? this.map : null })

    if (visible) {
      this.reconcilePositionMarkers()
    } else {
      const existing = this.positionMarkers[deviceKey]
      if (existing) {
        existing.forEach(marker => marker.setMap(null))
        existing.clear()
      }
    }
  }

  // Debounced entry point for map 'idle' — several idles can fire in quick
  // succession while a zoom/pan gesture settles, and each one recomputing
  // markers is exactly the kind of jank this is meant to avoid.
  updatePositionMarkers() {
    if (this.positionMarkersDebounce) clearTimeout(this.positionMarkersDebounce)
    this.positionMarkersDebounce = setTimeout(() => this.reconcilePositionMarkers(), 120)
  }

  // Re-evaluates which individual position markers should exist: none
  // below MIN_ZOOM_FOR_POSITION_MARKERS (the polyline's own IconSequence
  // carries the direction cue instead — see drawTracks), and only the
  // ones inside the current viewport above it. Reconciles rather than
  // clearing and rebuilding from scratch: a marker already on the map
  // stays untouched if it's still visible, so a small pan/zoom only pays
  // for the markers that actually entered or left the viewport instead of
  // recreating a full screenful of Marker objects every time.
  reconcilePositionMarkers() {
    const zoomedInEnough = this.map.getZoom() >= this.constructor.MIN_ZOOM_FOR_POSITION_MARKERS
    const bounds = zoomedInEnough ? this.map.getBounds() : null

    Object.entries(this.trackPaths).forEach(([ deviceKey, { path, validPositions, track } ]) => {
      const existing = this.positionMarkers[deviceKey] || (this.positionMarkers[deviceKey] = new Map())

      if (!bounds || this.trackVisible[deviceKey] === false) {
        existing.forEach(marker => marker.setMap(null))
        existing.clear()
        return
      }

      const visible = new Set()
      path.forEach((point, index) => { if (bounds.contains(point)) visible.add(index) })

      existing.forEach((marker, index) => {
        if (visible.has(index)) return

        marker.setMap(null)
        existing.delete(index)
      })

      visible.forEach(index => {
        if (existing.has(index)) return

        existing.set(index, this.createPositionMarker(path, index, validPositions, track, deviceKey))
      })
    })
  }

  // One small arrow marker for a single loaded position, pointing toward
  // the next position — lets every individual GPS reading be identified
  // and clicked for its own data, once it's actually rendered (see
  // #updatePositionMarkers for when that is).
  createPositionMarker(path, index, validPositions, track, deviceKey) {
    const point = path[index]
    const next = path[index + 1] || path[index - 1] || point
    const heading = google.maps.geometry.spherical.computeHeading(
      new google.maps.LatLng(point), new google.maps.LatLng(next)
    )

    const marker = new google.maps.Marker({
      position: point,
      map:      this.map,
      icon: {
        path:         google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
        scale:        3,
        rotation:     heading,
        fillColor:    '#ffffff',
        fillOpacity:  1,
        strokeColor:  track.color,
        strokeWeight: 1.5
      },
      zIndex: 3
    })
    marker.addListener('click', () => this.showInfoWindow(point, validPositions[index], track, deviceKey))

    return marker
  }

  // One marker per stopped/engine-off segment (see PreviousRoutesController
  // #track_stops) instead of plotting each jittery GPS reading — clicking
  // it shows how long the vehicle stayed there.
  createStopMarker(stop, track, deviceKey) {
    const wrapper = document.createElement('div')
    wrapper.className = tw('flex', 'items-center', 'justify-center', 'cursor-pointer', 'select-none')

    const pin = document.createElement('div')
    pin.className = tw(
      'w-6', 'h-6', 'flex', 'items-center', 'justify-center',
      'rounded-full', 'border-2', 'border-white', 'shadow-lg', 'bg-gray-500'
    )
    pin.innerHTML = '<span class="text-white text-xs leading-none">&#9679;</span>'
    wrapper.appendChild(pin)

    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: { lat: parseFloat(stop.lat), lng: parseFloat(stop.lng) },
      map:      this.map,
      content:  wrapper,
      zIndex:   1
    })
    marker.addListener('click', () => this.showStopInfoWindow(marker.position, stop, track))

    return marker
  }

  showStopInfoWindow(latLng, stop, track) {
    const stateLabel = stop.state === 'engine_off' ? 'Desligado' : 'Parado ligado'

    const content =
      `<div class="font-sans min-w-[200px] max-w-[280px] overflow-hidden rounded-xl">
         <div class="px-5 pt-4 pb-2">
           <span class="text-xs font-bold text-gray-900 uppercase tracking-wide">${stateLabel}</span>
         </div>
         <div class="px-5 pb-5 text-xs text-gray-700 space-y-1">
           <div>De ${stop.start_time} até ${stop.end_time}</div>
           <div class="font-medium text-gray-900">${this.formatStopDuration(stop.duration_seconds)}</div>
         </div>
       </div>`

    this.infoWindow.setContent(content)
    this.infoWindow.setPosition(latLng)
    this.infoWindow.open(this.map)
  }

  formatStopDuration(totalSeconds) {
    const minutes = Math.floor(totalSeconds / 60)
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60

    return hours > 0 ? `${hours}h${String(remainingMinutes).padStart(2, '0')}min` : `${remainingMinutes}min`
  }

  // Bigger, more visible teardrop-pin marker (visual style borrowed from
  // monitoring_map_controller.js's buildVehiclePinElement) plus a name-tag
  // chip identifying which device it belongs to. Keeps this file's own
  // tw()/Tailwind-class convention rather than monitoring's inline cssText.
  createEndpointMarker(position, label, track, deviceKey) {
    const wrapper = document.createElement('div')
    wrapper.className = tw('flex', 'flex-col', 'items-center', 'cursor-pointer', 'select-none', 'gap-0.5')

    const pin = document.createElement('div')
    pin.className = tw(
      'w-9', 'h-9', 'flex', 'items-center', 'justify-center',
      'rounded-full', 'rounded-bl-none', '-rotate-45',
      'border-2', 'border-white', 'shadow-lg'
    )
    pin.style.backgroundColor = track.color || '#3B82F6'

    const glyph = document.createElement('span')
    glyph.className = tw('rotate-45', 'text-white', 'font-extrabold', 'text-xs', 'leading-none')
    glyph.textContent = label
    pin.appendChild(glyph)
    wrapper.appendChild(pin)

    if (track.label) {
      const tag = document.createElement('span')
      tag.className = tw(
        'px-1.5', 'py-0.5', 'rounded', 'bg-gray-900/80', 'text-white',
        'text-[10px]', 'font-semibold', 'whitespace-nowrap', 'shadow'
      )
      tag.textContent = track.label
      wrapper.appendChild(tag)
    }

    return new google.maps.marker.AdvancedMarkerElement({ position, map: this.map, content: wrapper, zIndex: 3 })
  }

  findNearestPosition(positions, latLng) {
    let nearest = null
    let minDist = Infinity

    positions.forEach(p => {
      const dist = google.maps.geometry.spherical.computeDistanceBetween(
        latLng, new google.maps.LatLng(parseFloat(p.lat), parseFloat(p.lng))
      )
      if (dist < minDist) { minDist = dist; nearest = p }
    })

    return nearest
  }

  showInfoWindow(latLng, pos, track, deviceKey) {
    const item = (iconBgClass, iconEmoji, label, value) =>
      `<div class="flex items-center gap-2.5 py-1">
         <div class="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-lg ${iconBgClass}">
           <span class="text-sm leading-none">${iconEmoji}</span>
         </div>
         <div class="min-w-0 flex flex-col gap-0.5">
           <span class="text-[10px] font-semibold uppercase tracking-widest text-gray-400 leading-none">${label}</span>
           <span class="text-xs font-medium text-gray-900 leading-snug truncate">${value}</span>
         </div>
       </div>`

    const header = track?.label
      ? `<div class="flex items-center gap-2 px-5 pt-4 pb-2">
           <span class="w-2.5 h-2.5 rounded-full flex-shrink-0" style="background-color: ${track.color || '#6B7280'}"></span>
           <span class="text-xs font-bold text-gray-900 uppercase tracking-wide">${track.label}</span>
         </div>`
      : ''

    const engineOn    = !!pos.engine_started
    const badgeClass  = engineOn
      ? 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20'
      : 'bg-gray-100 text-gray-500 ring-1 ring-inset ring-gray-500/20'
    const dotColor    = engineOn ? '#10b981' : '#9ca3af'
    const engineLabel = engineOn ? 'Motor ligado' : 'Motor desligado'
    const engineBadge = pos.engine_started != null
      ? `<div class="mt-3 pt-2.5 border-t border-gray-100 flex">
           <span class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${badgeClass}">
             <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
               <circle cx="4" cy="4" r="4" fill="${dotColor}"/>
             </svg>
             ${engineLabel}
           </span>
         </div>`
      : ''

    const rows = [
      pos.ts                 ? item('bg-blue-50',   '🕐', 'Data / Hora',  pos.ts)                : null,
      pos.velocity != null   ? item('bg-orange-50', '⚡', 'Velocidade',   `${pos.velocity} km/h`) : null,
      pos.odometer           ? item('bg-violet-50', '🛣️', 'Odômetro',    pos.odometer)           : null,
      pos.nearest_fence_name ? item('bg-teal-50',   '📍', 'Ponto próximo', pos.nearest_fence_name) : null
    ].filter(Boolean)

    const content =
      `<div class="font-sans min-w-[220px] max-w-[300px] overflow-hidden rounded-xl">
         ${header}
         <div class="px-5 ${header ? 'pb-5' : 'pt-5 pb-5'}">
           <div class="divide-y divide-gray-50">${rows.join('')}</div>
           ${engineBadge}
         </div>
       </div>`

    this.infoWindow.setContent(content)
    this.infoWindow.setPosition(latLng)
    this.infoWindow.open(this.map)
  }

  fitBounds(path) {
    if (!this.map || !path?.length) return

    const bounds = new google.maps.LatLngBounds()
    path.forEach(point => bounds.extend(point))
    this.map.fitBounds(bounds, { top: 20, right: 20, bottom: 20, left: 20 })

    google.maps.event.addListenerOnce(this.map, 'idle', () => {
      if (this.map.getZoom() < 5) this.map.setZoom(5)
    })
  }

  clearTracks() {
    if (this.positionMarkersDebounce) clearTimeout(this.positionMarkersDebounce)
    Object.values(this.polylines).forEach(polyline => { if (polyline) polyline.setMap(null) })
    this.polylines = {}
    Object.values(this.pointMarkers).forEach(markers => markers.forEach(m => { if (m) m.map = null }))
    this.pointMarkers = {}
    Object.values(this.positionMarkers).forEach(markers => markers.forEach(m => { if (m) m.setMap(null) }))
    this.positionMarkers = {}
    this.trackPaths = {}
    this.trackVisible = {}
    Object.values(this.stopMarkers).forEach(markers => markers.forEach(m => { if (m) m.map = null }))
    this.stopMarkers = {}
    Object.values(this.replayMarkers).forEach(marker => { if (marker) marker.map = null })
    this.replayMarkers = {}
    this.replayIndexes = {}
    if (this.hasLegendTarget) { this.legendTarget.innerHTML = ''; this.legendTarget.classList.add('hidden') }
  }

  // ── Points-of-interest overlay ─────────────────────────────────────────────

  loadFences() {
    if (!this.hasFencesJsonUrlValue || this.fencesLoaded) return

    fetch(this.fencesJsonUrlValue, { headers: { Accept: 'application/json' } })
      .then(res => res.json())
      .then(fences => {
        this.fencesLoaded = true
        this.renderFences(fences)
      })
      .catch(err => console.error('Percurso: failed to load fences', err))
  }

  renderFences(fences) {
    if (!Array.isArray(fences) || !this.map) return

    fences.forEach(fence => {
      const center = this.fenceCenter(fence)
      if (!center) return

      const el = document.createElement('div')
      el.className = tw('w-3.5', 'h-3.5', 'rounded-full', 'ring-2', 'ring-white', 'shadow-sm')
      el.style.backgroundColor = fence.color || '#00A0A0'
      el.title = fence.name

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: center, map: this.map, content: el, zIndex: 1
      })
      this.fenceMarkers.push(marker)
    })
  }

  fenceCenter(fence) {
    if (fence.geometry?.center) return fence.geometry.center
    if (Array.isArray(fence.geometry?.coordinates) && fence.geometry.coordinates.length) {
      const [ ring ] = fence.geometry.coordinates
      if (!Array.isArray(ring) || !ring.length) return null

      const total = ring.reduce((acc, [ lng, lat ]) => ({ lat: acc.lat + lat, lng: acc.lng + lng }), { lat: 0, lng: 0 })
      return { lat: total.lat / ring.length, lng: total.lng / ring.length }
    }
    return null
  }

  clearFences() {
    this.fenceMarkers.forEach(m => { if (m) m.map = null })
    this.fenceMarkers = []
    this.fencesLoaded = false
  }

  // ── Replay API (used by percurso_replay_controller.js via outlet) ─────────
  // Keyed by deviceKey ('todos' or a device id string) so each DETALHE
  // panel's replay marker moves independently, colored to match its own
  // polyline. 'todos' has no color/polyline of its own (see drawTracks) so
  // it falls back to the original generic red marker over the flat,
  // chronologically-merged timeline.

  seekToPosition(deviceKey, index) {
    const track = this.tracksValue[deviceKey]
    if (!track) return

    const validPositions = (track.points || []).filter(p => p.lat && p.lng)
    if (!validPositions.length) return

    const clamped = Math.max(0, Math.min(index, validPositions.length - 1))
    this.replayIndexes[deviceKey] = clamped
    const pos = validPositions[clamped]
    const position = { lat: parseFloat(pos.lat), lng: parseFloat(pos.lng) }
    const color = track.color || '#EF4444'

    if (!this.replayMarkers[deviceKey]) {
      const el = document.createElement('div')
      el.className = tw('w-4', 'h-4', 'rounded-full', 'border-2', 'border-white', 'shadow-lg')
      el.style.backgroundColor = color
      el.style.boxShadow = `0 0 0 4px ${color}55`
      this.replayMarkers[deviceKey] = new google.maps.marker.AdvancedMarkerElement({ position, map: this.map, content: el, zIndex: 4 })
    } else {
      this.replayMarkers[deviceKey].position = position
    }

    this.dispatch('positionChanged', { detail: { deviceKey, index: clamped, position: pos } })
  }

  play(deviceKey) {
    if (this.replayTimers[deviceKey]) return
    const track = this.tracksValue[deviceKey]
    const total = (track?.points || []).filter(p => p.lat && p.lng).length
    if (!total) return

    this.replayTimers[deviceKey] = setInterval(() => {
      const next = (this.replayIndexes[deviceKey] ?? -1) + 1
      if (next >= total) { this.pause(deviceKey); return }
      this.seekToPosition(deviceKey, next)
    }, 300)
  }

  pause(deviceKey) {
    if (this.replayTimers[deviceKey]) {
      clearInterval(this.replayTimers[deviceKey])
      delete this.replayTimers[deviceKey]
    }
  }

  pauseAll() {
    Object.keys(this.replayTimers).forEach(deviceKey => this.pause(deviceKey))
  }

  // ── Map controls ──────────────────────────────────────────────────────────

  toggleMapTypeDropdown(event) {
    event.stopPropagation()
    if (this.hasMapTypeMenuTarget) this.mapTypeMenuTarget.classList.toggle('hidden')
    document.addEventListener('click', this.closeDropdown.bind(this), { once: true })
  }

  selectMapType(event) {
    const mapType = event.currentTarget.dataset.mapType
    this.currentMapType = mapType
    if (this.map) this.map.setMapTypeId(google.maps.MapTypeId[mapType.toUpperCase()])
    if (this.hasMapTypeMenuTarget) this.mapTypeMenuTarget.classList.add('hidden')
  }

  toggleFullscreen() {
    const el = this.element
    if (!document.fullscreenElement) {
      el.requestFullscreen().catch(err => console.warn('Fullscreen error:', err))
    } else {
      document.exitFullscreen()
    }
  }

  handleFullscreenChange() {
    const isFullscreen = !!document.fullscreenElement
    if (this.hasFullscreenExpandIconTarget)   this.fullscreenExpandIconTarget.classList.toggle('hidden', isFullscreen)
    if (this.hasFullscreenCollapseIconTarget) this.fullscreenCollapseIconTarget.classList.toggle('hidden', !isFullscreen)
  }

  closeDropdown() {
    if (this.hasMapTypeMenuTarget) this.mapTypeMenuTarget.classList.add('hidden')
  }
}
